import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { useRouter } from "@lib/i18n/navigation-pages-router";
import { useOpenDrawer } from "@hooks/use-drawer";
import {
  useUserShippingAddress,
  useUserCollectionAddress,
  useUpdateReturnCarrier,
  useIsLoadingAddresses,
} from "@hooks/use-addresses";
import { Button } from "@components/core";
import SharedAccountsLayout from "@components/core/SharedAccountsLayout";
import { showSuccessToast, showErrorToast } from "@utils/toasts/showToasts";
import carriers from "@utils/constants/carriers";
import CollectionModeChoice from "./CollectionModeChoice";
import { getReturnLink } from "@utils/getReturnLink";
import RelayPointsListMap from "@components/core/RelayPointsMap/RelayPointsListMap";

const Collection = () => {
  const t = useTranslations("addresses");

  const router = useRouter();
  const isLoadingAddresses = useIsLoadingAddresses();

  const openDrawer = useOpenDrawer();
  const updateReturnCarrier = useUpdateReturnCarrier();
  const userShippingAddress = useUserShippingAddress();
  const userCollectionAddress = useUserCollectionAddress();

  const [carrier, setCarrier] = useState(userCollectionAddress?.carrier_name || carriers.colissimo);
  const [loading, setLoading] = useState(false);

  async function handleCarrierSubmit() {
    try {
      setLoading(true);
      await updateReturnCarrier(carrier);

      if (router.query?.redirect) {
        router.replace(router.query.redirect).then(() => {
          showSuccessToast(
            t("collection_form.success.title"),
            t("collection_form.success.content"),
          );
          if (router.query.origin === "closetDrawer") openDrawer();
        });
      } else {
        router.back();
        setTimeout(() => {
          showSuccessToast(
            t("collection_form.success.title"),
            t("collection_form.success.content"),
          );
        }, 100);
      }
    } catch (err) {
      setLoading(false);
      showErrorToast(t("error_msg_default.title"), t("error_msg_default.content"));
    }
  }

  const handleCarrierChange = (e) => {
    setCarrier(e.target.value);
  };

  useEffect(() => {
    if (userCollectionAddress)
      setCarrier(userCollectionAddress?.carrier_name || carriers.colissimo);
  }, [userCollectionAddress]);

  useEffect(() => {
    if (!isLoadingAddresses && !userCollectionAddress) router.replace("/profile/addresses");
  }, [isLoadingAddresses, userCollectionAddress]);

  if (isLoadingAddresses) return null;

  const linkUrl = getReturnLink(carrier, userShippingAddress?.country);

  return (
    <>
      <SharedAccountsLayout title="collection_address_title" />
      <div className="container max-accounts-layout-size">
        <p className="text-center my-4">{t("collection_form.subtitle")}</p>

        <CollectionModeChoice
          onChange={handleCarrierChange}
          currentMode={carrier}
        />

        <Button
          fullWidth
          variant="primary"
          className="my-4"
          onClick={handleCarrierSubmit}
          loading={loading}
        >
          {t("collection_form.save")}
        </Button>

        {!linkUrl.startsWith("http") && (
          <>
            <h6>{t("collection_form.search_relay_point")}</h6>
            <RelayPointsListMap
              startingAddress={userShippingAddress}
              carriersToDisplay={[carrier]}
            />
          </>
        )}
      </div>
    </>
  );
};

export default Collection;
